import React, {useMemo, useState} from 'react';
import Wrapper from '../../components/Wrapper';
import DashboardHeader from '../../components/DashboardHeader';
import Input from '../../components/Input';
import {useTranslation} from '../../hooks/useTranslation';
import {
  useCreateEquipmentMutation,
  useLazyGetFacilityQuery,
  useLazyGetCurrentEquipmentQuery,
  useUpdateEquipmentMutation,
  useGetFuelTypesQuery,
  useCreateHoseEquipmentMutation,
  useUpdateHoseEquipmentMutation,
} from '../../store/builder/inspections.api';
import FacilitySelect from '../../components/EquipmentComponents/FacilitySelect';
import './index.scss';
import EquipmentSelect from '../../components/EquipmentComponents/EquipmentSelect';
import TextField from '../../components/EquipmentComponents/TextField';
import RadioField from '../../components/EquipmentComponents/RadioField';
import FileField from '../../components/EquipmentComponents/FileField';
import DateField from '../../components/EquipmentComponents/DateField';
import {useEffect} from 'react';
import {useDispatch} from 'react-redux';
import {setLoader} from '../../store/builder/reducer';
import {toast} from 'react-toastify';
import {useLocation, useNavigate, useParams} from 'react-router-dom';
import {useErrorHandler} from '../../global/ErrorsHandler';
import {Helmet} from 'react-helmet';
import SelectField from '../../components/EquipmentComponents/SelectField';
import FuelSelect from '../../components/EquipmentComponents/FuelSelect';
import moment from 'moment/moment';
import HoseElement from '../../components/HoseElement';
import {generateUUID} from '../../global/utils';

export default function CreateEquipment() {
  const {t} = useTranslation();
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const params = useParams();
  const location = useLocation()?.state?.equipment ?? params?.id;
  const locationS = useLocation();
  const [
    getEquipment,
    {
      data: getEquipmentData,
      error: getEquipmentError,
      isError: getEquipmentIsError,
      isLoading: getEquipmentIsLoading,
      isSuccess: getEquipmentIsSuccess,
    },
  ] = useLazyGetCurrentEquipmentQuery();
  const [title, setTitle] = useState('');
  const [allowDailyDec, setAllowDailyDec] = useState(false);
  const [stationsOptions, setStationsOptions] = useState([]);
  const [terminalsOptions, setTerminalsOptions] = useState([]);
  const [currentFacility, setCurrentFacility] = useState(null);
  const [currentStations, setCurrentStations] = useState(null);
  const [currentTerminals, setCurrentTerminals] = useState(null);
  const [currentEquipment, setCurrentEquipment] = useState(null);
  const [currentFuel, setCurrentFuel] = useState(null);
  const [fields, setFields] = useState([]);
  const [renderFields, setRenderFields] = useState([]);
  const [changesFields, setChangesFields] = useState([]);
  const [changed, setChanged] = useState(false);
  const [hasHose, setHasHose] = useState(false);
  const [hoseFields, setHoseFields] = useState([]);
  const [resHoseFields, setResHoseFields] = useState([]);
  const [uploadingCount, setUploadingCount] = useState(0);

  const [
    getFacility,
    {data: facilityData, isFetching: facilityIsLoading, error: faclityError},
  ] = useLazyGetFacilityQuery();
  const [
    createEquipment,
    {isFetching: createIsLoading, error: createError, isError: createIsError},
  ] = useCreateEquipmentMutation();
  const [
    updateEquipment,
    {isFetching: updateIsLoading, error: updateError, isError: updateIsError},
  ] = useUpdateEquipmentMutation();
  const [
    createHoseEquipment,
    {
      isFetching: createHoseEquipmentIsLoading,
      error: createHoseEquipmentError,
      isError: createHoseEquipmentIsError,
    },
  ] = useCreateHoseEquipmentMutation();

  const {
    data: fuelData,
    isFetching: fuelIsLoading,
    error: fuelError,
    isError: fuelIsError,
    isSuccess: fuelIsSuccess,
  } = useGetFuelTypesQuery();

  const [errorHandler, setErrorHandler] = useErrorHandler([
    updateError,
    createError,
    faclityError,
    createHoseEquipmentError,
  ]);
  useEffect(() => {
    getFacility();
    if (params?.id) {
      getEquipment(params.id);
    }
  }, [params]);

  useEffect(() => {
    if (getEquipmentData?.data?.has_hoses && location) {
      setHasHose(true);
    } else {
      setHasHose(false);
    }
  }, [getEquipmentData]);

  useEffect(() => {
    if (!changed) {
      if ((facilityIsLoading, updateIsLoading, getEquipmentIsLoading)) {
        dispatch(setLoader(true));
      } else {
        dispatch(setLoader(false));
      }

      if (getEquipmentIsSuccess) {
        if (!title?.length) {
          setTitle(getEquipmentData?.data?.title);
        }
        setCurrentFacility({
          value: getEquipmentData?.data?.facility?.id,
          label: getEquipmentData?.data?.facility?.name,
        });
        setCurrentEquipment({
          value: getEquipmentData?.data?.type?.id,
          label: getEquipmentData?.data?.type?.title,
        });
        setCurrentFuel({
          value: getEquipmentData?.data?.fuel_type?.id,
          label: getEquipmentData?.data?.fuel_type?.name,
        });
        setAllowDailyDec(getEquipmentData?.data?.allow_daily_dec);
        let changedArray = getEquipmentData?.data?.fields?.map(item => {
          if (item?.value && item?.type === 'date') {
            return {...item, value: moment(item?.value).format('YYYY-MM-DD')};
          }
          return item;
        });
        setFields(JSON.parse(JSON.stringify(changedArray)));
      }
    }
  }, [
    facilityIsLoading,
    updateIsLoading,
    getEquipmentIsSuccess,
    fuelIsSuccess,
  ]);

  useEffect(() => {
    setResHoseFields(
      getEquipmentData?.data?.hoses?.map(el => ({
        ...el,
        edit_status: 'no-edit',
      })),
    );
  }, [getEquipmentData]);

  const addTooltips = fields => {
    return fields.map(item => {
      if (item?.key === 'last_changed_date') {
        item = {
          ...item,
          tooltip: t('createEquipment.tooltips.lastChangedDate'),
        };
      } else if (item?.key === 'last_interior_date') {
        item = {
          ...item,
          tooltip: t('createEquipment.tooltips.lastInteriorDate'),
        };
      }
      return item;
    });
  };

  useEffect(() => {
    setRenderFields(addTooltips([...fields]));
  }, [fields]);

  // let test = renderFields?.sort(function(a, b) {
  //   let nameA = a?.label?.toUpperCase();
  //   let nameB = b?.label?.toUpperCase();
  //   if (nameA < nameB) {
  //     return -1;
  //   }
  //   if (nameA > nameB) {
  //     return 1;
  //   }
  //   return 0;
  // });

  function houseOrder(array) {
    const arr = [...array]; // the original array
    const idxHouseCount = arr.findIndex(obj => obj.key === 'hose_count');
    const idxHoseCerts = arr.findIndex(obj => obj.key === 'hose_certs');
    if (idxHouseCount > idxHoseCerts) {
      [arr[idxHouseCount], arr[idxHoseCerts]] = [
        arr[idxHoseCerts],
        arr[idxHouseCount],
      ];
    }
    return arr;
  }
  const preview = locationS?.state?.preview;

  const isFieldHidden = field => {
    if (!field?.parent_key) return false;
    const parent = renderFields?.find(f => f?.key === field.parent_key);
    if (!parent) return false;
    const selectedOption = parent?.options?.find(
      opt => String(opt?.value) === String(parent?.value),
    );
    return Boolean(selectedOption?.hide_children);
  };

  const submitHandler = () => {
    const orphanField = fields.find(f => {
      if (f.key !== 'differential_pressure_calibration_image') return false;
      const values = Array.isArray(f.value) ? f.value : f.value ? [f.value] : [];
      return values.some(uuid => !f.files?.find(file => file.uuid === uuid));
    });
    if (orphanField) {
      toast.error('Please remove the invalid image reference before saving');
      document
        .getElementById(orphanField.key)
        ?.closest('.equipment__container')
        ?.scrollIntoView({behavior: 'smooth', block: 'center'});
      return;
    }

    setChanged(true);
    let s = [];
    let data = {
      id: location?.id ? location?.id : location,
      title: title !== '' ? title : null,
      allow_daily_dec: allowDailyDec,
      equipment_type_id: currentEquipment?.value
        ? currentEquipment?.value
        : null,
      facility_id: currentFacility?.value ? currentFacility?.value : null,
      fuel_type_id: currentFuel?.value ? currentFuel?.value : null,
      fields: fields.filter(field => !isFieldHidden(field)),
    };

    for (let key in data) {
      if (!data[key] && key === data.facility_id) {
        toast?.error(t('createEquipment.messages.pleaseEnter') + ' ' + key);
        s.push(false);
      }
    }

    data = {
      ...data,
      station_id: currentStations?.value ? currentStations?.value : null,
      terminal_id: currentTerminals?.value ? currentTerminals?.value : null,
    };

    if (s.length === 0) {
      dispatch(setLoader(true));
      if (!location) {
        createEquipment(data)
          .then(res => {
            window.scrollTo({
              top: 0,
              behavior: 'smooth',
            });
            dispatch(setLoader(false));
            setChanged(false);
            toast.success(res.data.message);
            setFields([]);
            setTimeout(() => {
              navigate(-1);
            }, 500);
          })
          .catch(err => {});
      } else {
        updateEquipment({...data})
          ?.then(res => {
            window.scrollTo({
              top: 0,
              behavior: 'smooth',
            });
            dispatch(setLoader(false));
            setChanged(false);
            toast.success(res.data.message);
            setTimeout(() => {
              navigate(-1);
            }, 500);
          })
          .catch(err => toast.error(err?.response?.data?.message));
      }
    }
  };

  useEffect(() => {
    if (createError) {
      toast.error(createError?.data?.message);
    }
  }, [createError]);

  const addNewHose = () => {
    setHoseFields(old => [
      ...old,
      {
        name: '',
        cert_image: null,
        manufacture_date: null,
        edit_status: 'new',
        id: generateUUID(),
      },
    ]);
  };

  const renderCreatedHoses = useMemo(() => {
    return resHoseFields?.map((hose, index) => (
      <HoseElement
        key={index}
        hose={hose}
        index={index}
        setHoseFields={setResHoseFields}
        getEquipment={() => getEquipment(params.id)}
        readOnly={preview}
      />
    ));
  }, [resHoseFields, preview]);

  return (
    <Wrapper>
      <Helmet>
        <title>
          {params?.id ? t('createEquipment.editPageTitle') : t('createEquipment.pageTitle')}
        </title>
      </Helmet>
      <DashboardHeader
        title={location ? t('createEquipment.editTitle') : t('createEquipment.title')}
        type={'create-equipment'}
      />
      <div className='equipment new' style={{padding: 10}}>
        <div className='equipmentCreate'>
          <Input
            placeholder={t('createEquipment.idPlaceholder')}
            maxwidth={500}
            label={t('createEquipment.idLabel')}
            value={title}
            onInput={e => {
              setTitle(() => e.target.value);
            }}
            readOnly={preview}
            title={preview ? t('createEquipment.readOnly') : undefined}
          />
          <RadioField
            id={'allow_daily_dec'}
            key={'allow_daily_dec'}
            label={t('createEquipment.allowDailyDecLabel')}
            setFields={() => setAllowDailyDec(!allowDailyDec)}
            options={[{label: t('createEquipment.radioOptions.yes'), value: true}, {label: t('createEquipment.radioOptions.no'), value: false}]}
            field={{key: 'allow_daily_dec', value: allowDailyDec}}
            readOnly={preview}
          />
          <EquipmentSelect
            currentEquipment={currentEquipment}
            setCurrentEquipment={setCurrentEquipment}
            setFields={setFields}
            equipment={location}
            readOnly={preview || locationS?.pathname?.split('/')[1] !== 'create-equipment'}
          />
          {params?.id ? (
            <FacilitySelect
              currentFacility={currentFacility}
              setCurrentFacility={setCurrentFacility}
              facilityData={facilityData?.filter(el => el.can_edit === true)}
              setCurrentStations={setCurrentStations}
              setStationsOptions={setStationsOptions}
              setTerminalsOptions={setTerminalsOptions}
              readOnly={preview || params?.id}
            />
          ) : (
            currentEquipment?.has_facility !== false && (
              <FacilitySelect
                currentFacility={currentFacility}
                setCurrentFacility={setCurrentFacility}
                facilityData={facilityData?.filter(el => el.can_edit === true)}
                setCurrentStations={setCurrentStations}
                setStationsOptions={setStationsOptions}
                setTerminalsOptions={setTerminalsOptions}
                readOnly={preview || params?.id}
              />
            )
          )}
          <FuelSelect
            currentFacility={currentFuel}
            setCurrentFacility={setCurrentFuel}
            facilityData={fuelData}
            readOnly={preview}
          />
          {renderFields &&
            renderFields?.map(field => {
              if (isFieldHidden(field)) {
                return null;
              }
              if (field?.type === 'text' || field?.type === 'number') {
                return (
                  <TextField
                    key={field?.key}
                    id={field?.key}
                    label={field?.label}
                    setFields={setFields}
                    type={field?.type}
                    fields={fields}
                    value={field?.value ? field?.value : ''}
                    readOnly={preview || field?.readonly}
                  />
                );
              }
              if (field?.type === 'radio') {
                return (
                  <RadioField
                    id={field?.key}
                    key={field?.key}
                    label={field?.label}
                    setFields={setFields}
                    options={field?.options}
                    field={field}
                    readOnly={preview || field?.readonly}
                  />
                );
              }
              if (field?.type === 'date') {
                return (
                  <DateField
                    key={field?.key}
                    id={field?.key}
                    label={field?.label}
                    setFields={setFields}
                    field={field}
                    tooltip={field?.tooltip}
                    readOnly={preview || field?.readonly}
                  />
                );
              }
              if (field?.type === 'select') {
                return (
                  <SelectField
                    key={field?.key}
                    id={field?.key}
                    label={field?.label}
                    setFields={setFields}
                    field={field}
                    options={field.options}
                    readOnly={preview || field?.readonly}
                    placeholder={field?.placeholder}
                  />
                );
              }
              if (field?.type === 'file') {
                return (
                  <FileField
                    key={field?.key}
                    id={field?.key}
                    label={field?.label}
                    setFields={setFields}
                    equipmentId={params.id}
                    field={field}
                    readOnly={preview || field?.readonly}
                    onUploadingChange={isUploading =>
                      setUploadingCount(prev => isUploading ? prev + 1 : Math.max(0, prev - 1))
                    }
                  />
                );
              }
              if (field?.type === 'files') {
                return (
                  <FileField
                    key={field?.key}
                    id={field?.key}
                    label={field?.label}
                    setFields={setFields}
                    equipmentId={params.id}
                    field={field}
                    files={true}
                    fields={fields}
                    readOnly={preview || field?.readonly}
                    onUploadingChange={isUploading =>
                      setUploadingCount(prev => isUploading ? prev + 1 : Math.max(0, prev - 1))
                    }
                  />
                );
              }
            })}

          <div className='equipment__container'>
            <button
              onClick={submitHandler}
              className='equipment__button'
              disabled={changed || preview || uploadingCount > 0}
            >
              {location ? t('createEquipment.actions.save') : t('createEquipment.actions.create')}
            </button>
          </div>
        </div>
        {hasHose && (
          <div className='equipmentHose'>
            <div className='equipmentHose__list'>
              {preview ? null : renderCreatedHoses}
              {!preview && hoseFields?.map((hose, index) => (
                <HoseElement
                  key={index}
                  index={index}
                  hose={hose}
                  setHoseFields={setHoseFields}
                  getEquipment={() => getEquipment(params.id)}
                  readOnly={preview}
                />
              ))}
            </div>
            <div className='equipmentHose__btn' onClick={preview ? undefined : addNewHose} style={{ opacity: preview ? 0.5 : 1, cursor: preview ? 'not-allowed' : 'pointer' }}>
              <svg
                xmlns='http://www.w3.org/2000/svg'
                width='20'
                height='20'
                viewBox='0 0 20 20'
                fill='none'
              >
                <path
                  fillRule='evenodd'
                  clipRule='evenodd'
                  d='M16.3332 10.0003C16.3332 13.4981 13.4976 16.3337 9.99984 16.3337C6.50203 16.3337 3.6665 13.4981 3.6665 10.0003C3.6665 6.50252 6.50203 3.66699 9.99984 3.66699C13.4976 3.66699 16.3332 6.50252 16.3332 10.0003ZM18.3332 10.0003C18.3332 14.6027 14.6022 18.3337 9.99984 18.3337C5.39746 18.3337 1.6665 14.6027 1.6665 10.0003C1.6665 5.39795 5.39746 1.66699 9.99984 1.66699C14.6022 1.66699 18.3332 5.39795 18.3332 10.0003ZM9.99984 4.83366C10.5521 4.83366 10.9998 5.28137 10.9998 5.83366V9.00033H14.1665C14.7188 9.00033 15.1665 9.44804 15.1665 10.0003C15.1665 10.5526 14.7188 11.0003 14.1665 11.0003H10.9998V14.167C10.9998 14.7193 10.5521 15.167 9.99984 15.167C9.44755 15.167 8.99984 14.7193 8.99984 14.167V11.0003H5.83317C5.28089 11.0003 4.83317 10.5526 4.83317 10.0003C4.83317 9.44804 5.28089 9.00033 5.83317 9.00033H8.99984V5.83366C8.99984 5.28137 9.44755 4.83366 9.99984 4.83366Z'
                  fill='#27323C'
                />
              </svg>
              <p>{t('createEquipment.actions.addHose')}</p>
            </div>
            {/*{*/}
            {/*  hoseFields?.length > 0 &&*/}
            {/*  <div className="equipmentHose__btn submit" onClick={createHose}>*/}
            {/*    <p>Save hose changes</p>*/}
            {/*  </div>*/}
            {/*}*/}
          </div>
        )}
      </div>
    </Wrapper>
  );
}
